import { Activity, Palmtree } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ActivityReport, TimeOffReport } from "@/api/queries";
import { ActivityBar } from "@/components/common/ActivityBar";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { formatDayMonth, formatDuration, formatPeriod, formatShortDate, percentOf } from "@/lib/format";

function Share({ seconds, of, lang, tone }: { seconds: number; of: number; lang: string; tone: string }) {
  if (seconds === 0) return <span className="text-muted">—</span>;
  return (
    <span className="whitespace-nowrap tabular-nums">
      {formatDuration(seconds, lang)} <span className={tone}>· {percentOf(seconds, of)}%</span>
    </span>
  );
}

type ActivityReportViewProps = { data: ActivityReport; lang: string; toolbar: React.ReactNode };

/** Activity and productivity by employee and/or day. */
export function ActivityReportView({ data, lang, toolbar }: ActivityReportViewProps) {
  const { t } = useTranslation();
  const byUser = data.group_by.includes("user");
  const byDate = data.group_by.includes("date");
  const tot = data.totals;
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 pb-4">
        <div>
          <CardTitle>{t("reports.activity.title")}</CardTitle>
          <CardDescription>
            {t("reports.activity.hint", { period: formatPeriod(data.from, data.to, lang) })}
          </CardDescription>
        </div>
        {toolbar}
      </CardHeader>
      {data.rows.length === 0 ? (
        <EmptyState icon={Activity} title={t("reports.empty")} description={t("reports.emptyHint")} />
      ) : (
        <Table>
          <THead>
            <tr>
              {byUser && <TH>{t("reports.groups.user")}</TH>}
              {byDate && <TH>{t("reports.groups.date")}</TH>}
              {!byDate && <TH className="text-right">{t("reports.columns.days")}</TH>}
              <TH className="text-right">{t("reports.columns.hours")}</TH>
              <TH className="text-right">{t("reports.columns.manual")}</TH>
              <TH className="w-40">{t("reports.columns.activity")}</TH>
              <TH className="text-right">{t("reports.columns.productive")}</TH>
              <TH className="text-right">{t("reports.columns.unproductive")}</TH>
            </tr>
          </THead>
          <tbody>
            {data.rows.map((r) => {
              const worked = r.tracked_seconds + r.manual_seconds;
              return (
                <TR key={`${r.user?.id ?? ""}:${r.date ?? ""}`}>
                  {byUser && (
                    <TD>
                      <span className="flex min-w-0 items-center gap-2">
                        <UserAvatar name={r.user?.full_name ?? ""} className="size-6 text-[10px]" />
                        <span className="truncate font-medium">{r.user?.full_name}</span>
                      </span>
                    </TD>
                  )}
                  {byDate && (
                    <TD className="whitespace-nowrap first-letter:uppercase">
                      {r.date && formatShortDate(r.date, lang)}
                    </TD>
                  )}
                  {!byDate && <TD className="text-right text-muted tabular-nums">{r.days}</TD>}
                  <TD className="text-right font-medium whitespace-nowrap tabular-nums">
                    {formatDuration(worked, lang)}
                  </TD>
                  <TD className="text-right whitespace-nowrap text-muted tabular-nums">
                    {r.manual_seconds > 0 ? formatDuration(r.manual_seconds, lang) : "—"}
                  </TD>
                  <TD>
                    {r.tracked_seconds > 0 ? (
                      <ActivityBar percent={percentOf(r.active_seconds, r.tracked_seconds)} />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </TD>
                  <TD className="text-right">
                    <Share
                      seconds={r.productive_seconds}
                      of={r.tracked_seconds}
                      lang={lang}
                      tone="text-success"
                    />
                  </TD>
                  <TD className="text-right">
                    <Share
                      seconds={r.unproductive_seconds}
                      of={r.tracked_seconds}
                      lang={lang}
                      tone="text-danger"
                    />
                  </TD>
                </TR>
              );
            })}
          </tbody>
          <tfoot className="border-t bg-surface-muted/60 font-semibold">
            <tr>
              <td className="px-5 py-2.5" colSpan={(byUser ? 1 : 0) + (byDate ? 1 : 0) + (byDate ? 0 : 1)}>
                {t("reports.columns.total")}
              </td>
              <td className="px-5 py-2.5 text-right tabular-nums">
                {formatDuration(tot.tracked_seconds + tot.manual_seconds, lang)}
              </td>
              <td className="px-5 py-2.5 text-right tabular-nums">
                {tot.manual_seconds > 0 ? formatDuration(tot.manual_seconds, lang) : "—"}
              </td>
              <td className="px-5 py-2.5">
                <ActivityBar percent={percentOf(tot.active_seconds, tot.tracked_seconds)} />
              </td>
              <td className="px-5 py-2.5 text-right">
                <Share
                  seconds={tot.productive_seconds ?? 0}
                  of={tot.tracked_seconds}
                  lang={lang}
                  tone="text-success"
                />
              </td>
              <td className="px-5 py-2.5 text-right">
                <Share
                  seconds={tot.unproductive_seconds ?? 0}
                  of={tot.tracked_seconds}
                  lang={lang}
                  tone="text-danger"
                />
              </td>
            </tr>
          </tfoot>
        </Table>
      )}
    </Card>
  );
}

/** Vacation marks of the period, past ones included. */
export function TimeOffReportView({ data, lang }: { data: TimeOffReport; lang: string }) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <CardTitle>{t("reports.timeOff.title")}</CardTitle>
        <CardDescription>
          {t("reports.timeOff.hint", { period: formatPeriod(data.from, data.to, lang) })}
        </CardDescription>
      </CardHeader>
      {data.rows.length === 0 ? (
        <EmptyState icon={Palmtree} title={t("reports.timeOff.empty")} />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>{t("reports.groups.user")}</TH>
              <TH>{t("reports.timeOff.periods")}</TH>
              <TH className="text-right">{t("reports.timeOff.days")}</TH>
              <TH className="text-right">{t("reports.timeOff.workdays")}</TH>
            </tr>
          </THead>
          <tbody>
            {data.rows.map((r) => (
              <TR key={r.user.id}>
                <TD>
                  <span className="flex min-w-0 items-center gap-2">
                    <UserAvatar name={r.user.full_name} className="size-6 text-[10px]" />
                    <span className="truncate font-medium">{r.user.full_name}</span>
                  </span>
                </TD>
                <TD>
                  <span className="flex flex-wrap gap-1.5">
                    {r.periods.map((p) => (
                      <span
                        key={p.from}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium whitespace-nowrap text-primary"
                      >
                        <Palmtree className="size-3" aria-hidden="true" />
                        {p.from === p.until
                          ? formatDayMonth(p.from, lang)
                          : `${formatDayMonth(p.from, lang)} – ${formatDayMonth(p.until, lang)}`}
                      </span>
                    ))}
                  </span>
                </TD>
                <TD className="text-right font-medium tabular-nums">{r.days}</TD>
                <TD className="text-right text-muted tabular-nums">{r.workdays}</TD>
              </TR>
            ))}
          </tbody>
          <tfoot className="border-t bg-surface-muted/60 font-semibold">
            <tr>
              <td className="px-5 py-2.5" colSpan={2}>
                {t("reports.totalPeople", { count: data.rows.length })}
              </td>
              <td className="px-5 py-2.5 text-right tabular-nums">{data.total_days}</td>
              <td className="px-5 py-2.5 text-right tabular-nums">
                {data.rows.reduce((n, r) => n + r.workdays, 0)}
              </td>
            </tr>
          </tfoot>
        </Table>
      )}
    </Card>
  );
}
